import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { getJobOfferOrgDisplayName } from "@/features/job-offer/utils/get-job-offer-org-display";
import { useOrganization } from "@/hooks/use-organization";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { WageFrequency } from "@api/shared-runtime/enums";
import { OfferOrganizationWageSetLabel } from "@/features/job-offer/components/offer-organization-wage-set-label";
import { wageFrequencyToEnding } from "@/features/job-offer/utils/wage-frequency-to-ending";
import { formatAddress } from "@/features/job-offer/utils/format-address";
import { RenderHtml } from "@/components/RenderHtml";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { ChatDialog } from "@/features/chat/chat-dialog";
import { ScheduleSummary } from "./schedule-summary";
import { ProposalListItem } from "./proposal-list-item";
import { EditDatesDialog } from "./edit-dates-dialog";
import {
  MapPin,
  CalendarDays,
  DollarSign,
  Users,
  FileText,
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  X,
  Loader2,
  Trash2,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { format as formatDate, parseISO } from "date-fns";

export type JobOfferPreviewData = {
  id: string;
  title?: string | null;
  description?: string | null;
  facility?: {
    id: string;
    name?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
  } | null;
  // Wage rates (10 combinations: 5 base types × 2 overtime states)
  regularWage?: string | null;
  organizationWageSetName?: string | null;
  regularOvertimeWage?: string | null;
  weekendWage?: string | null;
  weekendOvertimeWage?: string | null;
  onCallWage?: string | null;
  onCallOvertimeWage?: string | null;
  onCallCallbackWage?: string | null;
  onCallCallbackOvertimeWage?: string | null;
  holidayWage?: string | null;
  holidayOvertimeWage?: string | null;
  wageFrequencyId?: WageFrequency | null;
  schedules?: Array<{
    id: string;
    startsAt: Date | string;
    durationMinutes: number;
    rrule?: string | null;
    rruleEndsAt?: Date | string | null;
  }>;
  shifts?: Array<{
    id: string;
    scheduleId: string | null;
    startsAt: Date | string;
    endsAt: Date | string;
    status: string;
    claimedByUserId?: string | null;
    claimedAt?: Date | string | null;
  }>;
  timezoneId?: string | null;
  canceledByOrganizationBusinessUnitAt?: string | null;
  decisions?: Array<{
    providerDecision?: boolean | null;
    providerDecisionAt?: string | null;
    providerCanceledAt?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    userId: string;
  }>;
  userShares?: Array<{
    id: string;
    firstName?: string | null;
    lastName?: string | null;
  }>;
  providerPoolShares?: Array<{
    id: string;
    providerPoolName?: string | null;
  }>;
  proposals?: Array<{
    id: string;
    status: string;
    initiatedAt: Date | string;
    initiatedByUserId: string;
    initiatedByRole: string;
    expiresAt: Date | string | null;
    scheduleCount: number;
    shiftCancelCount: number;
  }>;
  // Dates that pending proposals would add; not shifts until accepted
  pendingProposalShifts?: Array<{
    proposalId: string;
    startsAt: Date | string;
    endsAt: Date | string;
  }>;
};

type WageRateKey =
  | "regularWage"
  | "regularOvertimeWage"
  | "weekendWage"
  | "weekendOvertimeWage"
  | "onCallWage"
  | "onCallOvertimeWage"
  | "onCallCallbackWage"
  | "onCallCallbackOvertimeWage"
  | "holidayWage"
  | "holidayOvertimeWage";

const WAGE_RATE_ROWS: ReadonlyArray<{
  key: WageRateKey;
  label: string;
}> = [
  { key: "regularWage", label: "Regular" },
  { key: "regularOvertimeWage", label: "Regular OT" },
  { key: "weekendWage", label: "Weekend" },
  { key: "weekendOvertimeWage", label: "Weekend OT" },
  { key: "onCallWage", label: "On-Call" },
  { key: "onCallOvertimeWage", label: "On-Call OT" },
  { key: "onCallCallbackWage", label: "Callback" },
  { key: "onCallCallbackOvertimeWage", label: "Callback OT" },
  { key: "holidayWage", label: "Holiday" },
  { key: "holidayOvertimeWage", label: "Holiday OT" },
];

type JobOfferStatus = "accepted" | "pending" | "canceled";

type JobOfferPreviewDialogProps = {
  jobOffer: JobOfferPreviewData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestEdit?: (jobOfferId: string) => void;
  onCanceled?: () => void;
  user: { is_admin: boolean } | null | undefined;
};

const getJobOfferStatus = (jobOffer: JobOfferPreviewData): JobOfferStatus => {
  if (jobOffer.canceledByOrganizationBusinessUnitAt) {
    return "canceled";
  }

  const acceptedDecision = jobOffer.decisions?.find(
    (decision) => decision.providerDecision === true,
  );

  if (acceptedDecision?.providerCanceledAt) {
    return "canceled";
  }

  if (acceptedDecision) {
    return "accepted";
  }

  return "pending";
};

const isJobInPast = (jobOffer: JobOfferPreviewData): boolean => {
  const shifts = jobOffer.shifts;
  if (!shifts || shifts.length === 0) {
    return false;
  }

  const latestEnd = shifts.reduce((latest, shift) => {
    const candidate =
      typeof shift.endsAt === "string" ? parseISO(shift.endsAt) : shift.endsAt;
    return candidate > latest ? candidate : latest;
  }, new Date(0));

  return latestEnd < new Date();
};

const getEditState = (
  jobOffer: JobOfferPreviewData,
): { canEdit: boolean; reason?: string } => {
  if (jobOffer.canceledByOrganizationBusinessUnitAt) {
    return { canEdit: false, reason: "This job offer was canceled." };
  }

  const acceptedDecision = jobOffer.decisions?.find(
    (decision) => decision.providerDecision === true,
  );

  if (acceptedDecision?.providerCanceledAt) {
    return {
      canEdit: false,
      reason:
        "The accepting provider canceled this offer. Please create a new one if needed.",
    };
  }

  if (acceptedDecision) {
    return {
      canEdit: false,
      reason: "This job offer has already been accepted.",
    };
  }

  if (isJobInPast(jobOffer)) {
    return { canEdit: false, reason: "All scheduled dates are in the past." };
  }

  return { canEdit: true };
};

export const JobOfferPreviewDialog: React.FC<JobOfferPreviewDialogProps> = ({
  jobOffer,
  open,
  onOpenChange,
  onRequestEdit,
  onCanceled,
  user,
}) => {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);

  const [profileModalUserId, setProfileModalUserId] = React.useState<
    string | null
  >(null);
  const [chatUser, setChatUser] = React.useState<{
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [showAllRecipients, setShowAllRecipients] = React.useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] =
    React.useState(false);
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);
  const [showEditDatesDialog, setShowEditDatesDialog] = React.useState(false);
  const [initialCancelShiftIds, setInitialCancelShiftIds] = React.useState<
    string[]
  >([]);
  const [showCancelShiftChoiceDialog, setShowCancelShiftChoiceDialog] =
    React.useState(false);
  const [cancelShiftStep, setCancelShiftStep] = React.useState<
    "choose" | "direct"
  >("choose");
  const [pendingCancelShiftId, setPendingCancelShiftId] = React.useState<
    string | null
  >(null);
  const [cancelShiftsNote, setCancelShiftsNote] = React.useState("");

  const cancelJobOfferMutation = useMutation(
    trpc.jobOffer.organizationCancel.mutationOptions({
      onSuccess: () => {
        setShowCancelDialog(false);
        onOpenChange(false);
        onCanceled?.();
      },
    }),
  );

  const cancelShiftsMutation = useMutation(
    trpc.jobOffer.organizationCancelShifts.mutationOptions({
      onSuccess: async () => {
        setShowCancelShiftChoiceDialog(false);
        setPendingCancelShiftId(null);
        setCancelShiftsNote("");
        await queryClient.invalidateQueries({
          queryKey: trpc.jobOffer.organizationGetAll.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.jobOffer.organizationGet.queryKey(),
        });
      },
    }),
  );

  // Determine if cancel is available
  const canCancel = React.useMemo(() => {
    if (!jobOffer) {
      return false;
    }

    // Check permission first
    if (!hasPermission(Permission.ManageJobOffers)) {
      return false;
    }

    // Already canceled by org
    if (jobOffer.canceledByOrganizationBusinessUnitAt) {
      return false;
    }

    // Already canceled by provider
    const acceptedDecision = jobOffer.decisions?.find(
      (decision) => decision.providerDecision === true,
    );
    if (acceptedDecision?.providerCanceledAt) {
      return false;
    }

    // All shifts in past
    if (isJobInPast(jobOffer)) {
      return false;
    }

    return true;
  }, [jobOffer, hasPermission]);

  const canAddDates = React.useMemo(() => {
    if (!jobOffer) {
      return false;
    }
    if (!hasPermission(Permission.ManageJobOffers)) {
      return false;
    }
    if (jobOffer.canceledByOrganizationBusinessUnitAt) {
      return false;
    }
    return true;
  }, [jobOffer, hasPermission]);

  // Reset state when job offer changes
  React.useEffect(() => {
    setShowAllRecipients(false);
    setIsDescriptionExpanded(false);
    setShowCancelDialog(false);
    setShowEditDatesDialog(false);
    setInitialCancelShiftIds([]);
    setShowCancelShiftChoiceDialog(false);
    setCancelShiftStep("choose");
    setPendingCancelShiftId(null);
    setCancelShiftsNote("");
  }, [jobOffer?.id]);

  const { data: recipientsData, isLoading: recipientsLoading } = useQuery(
    trpc.jobOffer.organizationRecipients.queryOptions(
      {
        jobOfferId: jobOffer?.id ?? "",
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id ?? "",
      },
      {
        enabled: !!jobOffer?.id && !!selectedOrgBusinessUnit?.id && open,
      },
    ),
  );

  if (!jobOffer) {
    return null;
  }

  const status = getJobOfferStatus(jobOffer);
  const displayInfo = getJobOfferOrgDisplayName(jobOffer);
  const headerGradient =
    status === "accepted"
      ? "from-green-600 to-emerald-700"
      : status === "canceled"
        ? "from-red-600 to-rose-700"
        : "from-primary to-primary/90";
  const statusLabel =
    status === "accepted"
      ? "Accepted"
      : status === "canceled"
        ? "Canceled"
        : "Waiting for provider";

  const editState = getEditState(jobOffer);
  const hasCompensation = WAGE_RATE_ROWS.some(({ key }) => jobOffer[key]);
  const wageFrequencyEnding = jobOffer.wageFrequencyId
    ? ` ${wageFrequencyToEnding[jobOffer.wageFrequencyId]}`
    : "";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto rounded-2xl border-slate-200 p-0 sm:rounded-2xl"
          showCloseButton={false}
        >
          {/* Status-Colored Header */}
          <div
            className={`bg-gradient-to-r ${headerGradient} px-6 py-5 rounded-t-2xl relative`}
          >
            <DialogClose className="absolute right-3 top-3 rounded-full p-1 text-white/80 hover:text-white hover:bg-white/20 transition-colors">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
            <div className="flex flex-col items-start justify-between gap-3 pr-8 sm:flex-row">
              <div className="min-w-0 flex-1">
                {/* Primary: Provider name (for accepted) or pool/provider list (for pending) */}
                <DialogTitle
                  className="text-xl font-bold text-white [overflow-wrap:anywhere]"
                  title={displayInfo.tooltip}
                >
                  {displayInfo.primaryName}
                </DialogTitle>
                {/* Secondary: Job title */}
                {displayInfo.secondaryInfo && (
                  <p className="text-sm text-white/80 mt-0.5 [overflow-wrap:anywhere]">
                    {displayInfo.secondaryInfo}
                  </p>
                )}
                {/* Tertiary: Facility name */}
                {jobOffer.facility?.name && (
                  <p className="text-xs text-white/80 mt-0.5 [overflow-wrap:anywhere]">
                    {jobOffer.facility.name}
                  </p>
                )}
              </div>
              <Badge
                variant="secondary"
                className="bg-white/20 text-white border-0 uppercase text-xs font-semibold shrink-0"
              >
                {statusLabel}
              </Badge>
            </div>
          </div>

          {/* Content Sections */}
          <div className="p-6 space-y-4 bg-slate-50">
            {/* Pending Provider Proposals Banner */}
            {(() => {
              const pendingProviderProposals = jobOffer.proposals?.filter(
                (p) =>
                  p.status === "pending" && p.initiatedByRole === "provider",
              );
              if (!pendingProviderProposals?.length) {
                return null;
              }
              return (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800">
                      {pendingProviderProposals.length === 1
                        ? "A provider has proposed changes that need your response"
                        : `${pendingProviderProposals.length} provider proposals need your response`}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {pendingProviderProposals.map((proposal) => (
                      <ProposalListItem
                        key={proposal.id}
                        proposal={proposal}
                        jobOfferId={jobOffer.id}
                        defaultExpanded
                        canRespond={hasPermission(Permission.ManageJobOffers)}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Location Section */}
            {jobOffer.facility && (
              <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Location
                  </span>
                </div>
                <p className="text-sm text-slate-700">
                  {formatAddress({
                    address1: jobOffer.facility.address1 ?? null,
                    address2: jobOffer.facility.address2 ?? null,
                    city: jobOffer.facility.city ?? null,
                    state: jobOffer.facility.state ?? null,
                    zipCode: jobOffer.facility.zipCode ?? null,
                  })}
                </p>
              </div>
            )}

            {/* Schedule / Shifts Section */}
            {(jobOffer.schedules || jobOffer.shifts) && (
              <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                {jobOffer.shifts && jobOffer.shifts.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Shifts
                      </span>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {[...jobOffer.shifts]
                        .sort((a, b) => {
                          const dateA =
                            typeof a.startsAt === "string"
                              ? new Date(a.startsAt)
                              : a.startsAt;
                          const dateB =
                            typeof b.startsAt === "string"
                              ? new Date(b.startsAt)
                              : b.startsAt;
                          return dateA.getTime() - dateB.getTime();
                        })
                        .map((shift) => {
                          const isCanceled = shift.status === "canceled";
                          const startsAt =
                            typeof shift.startsAt === "string"
                              ? parseISO(shift.startsAt)
                              : shift.startsAt;
                          const endsAt =
                            typeof shift.endsAt === "string"
                              ? parseISO(shift.endsAt)
                              : shift.endsAt;
                          return (
                            <div
                              key={shift.id}
                              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                                isCanceled
                                  ? "border-slate-100 bg-slate-50 text-slate-400 line-through"
                                  : "border-slate-100 bg-slate-50 text-slate-700"
                              }`}
                            >
                              <span>
                                {formatDate(startsAt, "MMM d, yyyy")} &middot;{" "}
                                {formatDate(startsAt, "h:mm a")} &ndash;{" "}
                                {formatDate(endsAt, "h:mm a")}
                              </span>
                              {!isCanceled && canCancel && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                                  onClick={() => {
                                    setPendingCancelShiftId(shift.id);
                                    setShowCancelShiftChoiceDialog(true);
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {isCanceled && (
                                <Badge
                                  variant="outline"
                                  className="text-xs text-slate-400 border-slate-200"
                                >
                                  Canceled
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </>
                ) : (
                  <ScheduleSummary
                    schedules={jobOffer.schedules}
                    shifts={jobOffer.shifts}
                  />
                )}
              </div>
            )}

            {/* Proposals Section (excludes pending provider proposals shown in banner above) */}
            {(() => {
              const otherProposals = jobOffer.proposals?.filter(
                (p) =>
                  !(p.status === "pending" && p.initiatedByRole === "provider"),
              );
              if (!otherProposals?.length) {
                return null;
              }
              return (
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarDays className="h-4 w-4 text-amber-500" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Proposals
                    </span>
                  </div>
                  <div className="space-y-2">
                    {otherProposals.map((proposal) => (
                      <ProposalListItem
                        key={proposal.id}
                        proposal={proposal}
                        jobOfferId={jobOffer.id}
                        canRespond={hasPermission(Permission.ManageJobOffers)}
                      />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Compensation Section */}
            {hasCompensation && (
              <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="h-4 w-4 text-green-500" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Compensation
                  </span>
                </div>
                <OfferOrganizationWageSetLabel
                  organizationWageSetName={jobOffer.organizationWageSetName}
                  className="mb-3 text-xs text-slate-500"
                />
                <div className="grid gap-2 text-sm">
                  {WAGE_RATE_ROWS.map(({ key, label }) => {
                    const amount = jobOffer[key];
                    if (!amount) {
                      return null;
                    }

                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between"
                      >
                        <span className="text-slate-500">{label}</span>
                        <span className="font-medium text-slate-900">
                          ${amount}
                          {wageFrequencyEnding}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Description Section (Expandable) */}
            {jobOffer.description && (
              <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <button
                  type="button"
                  className="flex items-center justify-between w-full"
                  onClick={() =>
                    setIsDescriptionExpanded(!isDescriptionExpanded)
                  }
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Description
                    </span>
                  </div>
                  {isDescriptionExpanded ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {isDescriptionExpanded && (
                  <div className="mt-3 text-sm text-slate-700 prose prose-sm max-w-none">
                    <RenderHtml html={jobOffer.description} />
                  </div>
                )}
              </div>
            )}

            {/* Shared Recipients Section */}
            <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Shared recipients
                  </span>
                </div>
                {recipientsData?.allRecipients?.length ? (
                  <Badge variant="secondary" className="text-[11px]">
                    {recipientsData.allRecipients.length} total
                  </Badge>
                ) : null}
              </div>
              {recipientsLoading ? (
                <p className="text-sm text-slate-500">Loading recipients...</p>
              ) : recipientsData ? (
                <div className="space-y-2">
                  {recipientsData.allRecipients.length > 0 ? (
                    <>
                      <div className="space-y-1.5">
                        {(showAllRecipients
                          ? recipientsData.allRecipients
                          : recipientsData.allRecipients.slice(0, 5)
                        ).map((recipient) => (
                          <div
                            key={`all-${recipient.id}`}
                            className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="block text-sm font-medium text-slate-800 truncate">
                                {[recipient.firstName, recipient.lastName]
                                  .filter(Boolean)
                                  .join(" ") || "Provider"}
                              </span>
                              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                                <span>
                                  {recipient.viaDirect
                                    ? "Direct share"
                                    : "Pool share"}
                                  {recipient.viaPools?.length
                                    ? ` via ${recipient.viaPools.join(", ")}`
                                    : ""}
                                </span>
                                {recipient.providerDecision != null ? (
                                  <span className="inline-flex items-center gap-1">
                                    {recipient.providerDecision ? (
                                      <>
                                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                                        <span className="text-emerald-600 font-medium">
                                          Accepted
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <XCircle className="h-3 w-3 text-rose-500" />
                                        <span className="text-rose-600 font-medium">
                                          Declined
                                        </span>
                                      </>
                                    )}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-slate-400">
                                    <Clock className="h-3 w-3" />
                                    <span>Pending</span>
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2 shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={() =>
                                  recipient.id &&
                                  setProfileModalUserId(recipient.id)
                                }
                              >
                                Profile
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="h-8"
                                onClick={() =>
                                  setChatUser({
                                    id: recipient.id!,
                                    firstName: recipient.firstName,
                                    lastName: recipient.lastName,
                                  })
                                }
                              >
                                <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                Chat
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {recipientsData.allRecipients.length > 5 && (
                        <button
                          type="button"
                          className="text-xs text-primary hover:text-primary/80 font-medium"
                          onClick={() =>
                            setShowAllRecipients(!showAllRecipients)
                          }
                        >
                          {showAllRecipients
                            ? "Show less"
                            : `View all ${recipientsData.allRecipients.length} recipients`}
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-500">
                      This offer has not been shared with any providers yet.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Unable to load recipients.
                </p>
              )}
            </div>

            {/* Action Section */}
            <div className="space-y-3">
              {/* Propose Date Changes Button */}
              {canAddDates && (
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                  onClick={() => {
                    setInitialCancelShiftIds([]);
                    setShowEditDatesDialog(true);
                  }}
                >
                  <CalendarDays className="h-4 w-4" />
                  Propose date changes
                </Button>
              )}

              {/* Cancel Button - Show prominently when offer can be canceled */}
              {canCancel && (
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={cancelJobOfferMutation.isPending}
                >
                  {cancelJobOfferMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  Cancel job offer
                </Button>
              )}

              {/* Edit Button */}
              {onRequestEdit && (
                <>
                  {editState.canEdit ? (
                    <Button
                      className="w-full flex items-center justify-center gap-2"
                      onClick={() => {
                        onRequestEdit(jobOffer.id);
                        onOpenChange(false);
                      }}
                    >
                      Edit job offer
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            Editing disabled
                          </p>
                          <p className="text-sm text-amber-700 mt-0.5">
                            {editState.reason}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this offer?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The offer "
              {jobOffer?.title || "Job offer"}" will be withdrawn for all
              recipients.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowCancelDialog(false)}>
              Go back
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (jobOffer?.id && selectedOrgBusinessUnit?.id) {
                  cancelJobOfferMutation.mutate({
                    jobOfferId: jobOffer.id,
                    organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                  });
                }
              }}
              disabled={cancelJobOfferMutation.isPending}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {cancelJobOfferMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Yes, cancel offer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Shift Choice Dialog - encourages proposal over direct cancel */}
      <Dialog
        open={showCancelShiftChoiceDialog}
        onOpenChange={(open) => {
          setShowCancelShiftChoiceDialog(open);
          if (!open) {
            setCancelShiftStep("choose");
            setPendingCancelShiftId(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          {cancelShiftStep === "choose" ? (
            <>
              <DialogHeader>
                <DialogTitle>Cancel this shift?</DialogTitle>
                <DialogDescription>
                  Proposing the cancellation asks the provider to confirm and
                  acknowledge the change before it takes effect. You can also
                  cancel directly without their confirmation.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  className="w-full"
                  onClick={() => {
                    if (pendingCancelShiftId) {
                      setInitialCancelShiftIds([pendingCancelShiftId]);
                      setShowCancelShiftChoiceDialog(false);
                      setCancelShiftStep("choose");
                      setPendingCancelShiftId(null);
                      setShowEditDatesDialog(true);
                    }
                  }}
                >
                  Propose cancellation
                </Button>
                <Button
                  variant="outline"
                  className="w-full text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => setCancelShiftStep("direct")}
                >
                  Cancel without confirming
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setShowCancelShiftChoiceDialog(false);
                    setCancelShiftStep("choose");
                    setPendingCancelShiftId(null);
                  }}
                >
                  Go back
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Cancel without confirming?</DialogTitle>
                <DialogDescription>
                  This will immediately cancel the shift. The provider will be
                  notified but won&apos;t need to accept.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label htmlFor="cancel-shifts-note">Note (optional)</Label>
                <Textarea
                  id="cancel-shifts-note"
                  placeholder="Add a note for the provider..."
                  value={cancelShiftsNote}
                  onChange={(e) => setCancelShiftsNote(e.target.value)}
                  rows={2}
                />
              </div>
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  className="w-full bg-rose-600 hover:bg-rose-700"
                  onClick={() => {
                    if (
                      jobOffer?.id &&
                      selectedOrgBusinessUnit?.id &&
                      pendingCancelShiftId
                    ) {
                      cancelShiftsMutation.mutate({
                        jobOfferId: jobOffer.id,
                        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                        shiftIds: [pendingCancelShiftId],
                        note: cancelShiftsNote || undefined,
                      });
                    }
                  }}
                  disabled={cancelShiftsMutation.isPending}
                >
                  {cancelShiftsMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Yes, cancel shift
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setCancelShiftStep("choose")}
                >
                  Go back
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {profileModalUserId && (
        <ProfileModal
          userId={profileModalUserId}
          onClose={() => setProfileModalUserId(null)}
          organizationBusinessUnitId={selectedOrgBusinessUnit?.id}
        />
      )}

      {chatUser && (
        <ChatDialog
          isOpen={!!chatUser}
          onOpenChange={(open) => !open && setChatUser(null)}
          user={{
            id: chatUser.id,
            firstName: chatUser.firstName,
            lastName: chatUser.lastName,
          }}
        />
      )}

      {jobOffer && (
        <EditDatesDialog
          open={showEditDatesDialog}
          onOpenChange={setShowEditDatesDialog}
          jobOfferId={jobOffer.id}
          shifts={jobOffer.shifts}
          initialCancelShiftIds={initialCancelShiftIds}
        />
      )}
    </>
  );
};
